import { useEffect, type RefObject } from 'react'

/** Everything Tab can land on. `summary` is on the list because the first-run
 *  sheet ends on one ("Other models"): missing, the wrap never saw its last
 *  stop, and every fourth Tab left the sheet for the page behind it. */
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[contenteditable="true"],[tabindex]:not([tabindex="-1"])'

/** Whether Tab could land on it. A closed `<details>` hides its content with
 *  `content-visibility`, which keeps layout boxes — so a box test counted the
 *  eight model fields folded under "Other models" as the sheet's last stops, and
 *  the wrap waited for a focus that never came. `checkVisibility` knows. */
const shown = (el: HTMLElement): boolean =>
  typeof el.checkVisibility === 'function'
    ? el.checkVisibility({ visibilityProperty: true })
    : el.offsetParent !== null || el.getClientRects().length > 0

/**
 * Focus containment for a modal dialog.
 *
 * `aria-modal` tells a screen reader the background is gone; it does nothing for
 * the Tab key. Without this a keyboard user tabs straight out of the sheet and
 * onto the canvas controls behind the scrim — 58 of them, on the splash — which
 * are invisible under the blur and out of reach of the pointer. That is the one
 * confusion a modal exists to prevent.
 *
 * So while the dialog is open:
 *
 *  - the app stage is made `inert`, which drops every focusable behind the modal
 *    out of the tab order AND the accessibility tree in one move. The dialogs
 *    render as siblings of `main.stage`, not children, so they stay live — and
 *    only one is ever open at a time, so a single flag is enough. This is the
 *    whole background half of the fix; the rest is courtesy;
 *  - focus moves into the dialog on open (the container itself, which carries
 *    `tabIndex={-1}` — so the screen reader announces the dialog's own label
 *    before the first control, rather than landing mid-sheet);
 *  - Tab and Shift+Tab wrap within it, so focus never escapes to the address bar
 *    and back;
 *  - on close focus returns to whatever opened it.
 *
 * `active` rather than relying on mount, so a sheet that renders `null` while
 * closed still runs the cleanup the frame it leaves — the effect keys on the
 * open flag, not on the component's life.
 */
export function useModalFocus<T extends HTMLElement>(ref: RefObject<T | null>, active: boolean) {
  useEffect(() => {
    const node = ref.current
    if (!active || !node) return

    const restore = document.activeElement as HTMLElement | null
    const stage = document.querySelector<HTMLElement>('main.stage')
    if (stage) stage.inert = true
    node.focus()

    const onKeydown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(shown)
      if (items.length === 0) {
        // A dialog with nothing to focus keeps focus on itself rather than
        // letting Tab walk out onto the inert background's ghost.
        e.preventDefault()
        node.focus()
        return
      }
      const first = items[0]!
      const last = items[items.length - 1]!
      const here = document.activeElement
      if (e.shiftKey && (here === first || here === node)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && here === last) {
        e.preventDefault()
        first.focus()
      }
    }
    node.addEventListener('keydown', onKeydown)

    return () => {
      node.removeEventListener('keydown', onKeydown)
      if (stage) stage.inert = false
      // Put focus back where it was — but only if that element is still on the
      // page. The splash opens with focus on nothing in particular, and there is
      // nothing to restore to there.
      if (restore && restore.isConnected) restore.focus()
    }
  }, [active, ref])
}
